import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedModule } from 'src/app/shared/shared.module';

/**
 * Callers MUST switch on the exact literal. Both values are truthy, so `if (result)` would
 * treat "keep my emails" as "delete them" — the bug this dialog exists to prevent.
 */
export type InboxDisconnectChoice = 'disconnect_only' | 'delete_connection';

export interface InboxDisconnectDialogData {
  /** Shown in the body, so the user sees which inbox they are acting on. */
  email: string;
  /** False when already disconnected — hides "Disconnect only". */
  canDisconnect: boolean;
  /** False when the user may not permanently delete this connection. */
  canDelete: boolean;
}

/**
 * Choose between a reversible disconnect and a permanent delete.
 *
 * Dedicated rather than the shared confirmation dialog, whose `close(true | false)`
 * contract cannot carry a third state safely, and which has no room for the per-choice
 * explanatory line that makes the destructive option hard to pick by accident.
 *
 * The choice is a radio selection with a single confirm button rather than one button
 * per option: the button then states exactly what is about to happen ("Remove all data"),
 * and the destructive path takes two deliberate clicks instead of one.
 */
@Component({
  selector: 'app-inbox-disconnect-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, UiModule, SharedModule],
  templateUrl: './inbox-disconnect-dialog.component.html',
  styleUrls: ['./inbox-disconnect-dialog.component.scss'],
})
export class InboxDisconnectDialogComponent {
  /** Defaults to the reversible option whenever it is offered. */
  choice: InboxDisconnectChoice;

  constructor(
    private dialogRef: MatDialogRef<InboxDisconnectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InboxDisconnectDialogData,
  ) {
    this.choice = data.canDisconnect ? 'disconnect_only' : 'delete_connection';
  }

  get isDestructive(): boolean {
    return this.choice === 'delete_connection';
  }

  /** The title follows the selection, so the heading never contradicts the button. */
  get titleKey(): string {
    return this.isDestructive
      ? 'smart_inbox.disconnect_dialog.title_delete'
      : 'smart_inbox.disconnect_dialog.title';
  }

  get confirmLabelKey(): string {
    return this.isDestructive
      ? 'smart_inbox.disconnect_dialog.confirm_delete'
      : 'smart_inbox.disconnect_dialog.confirm_disconnect_only';
  }

  select(choice: InboxDisconnectChoice): void {
    this.choice = choice;
  }

  confirm(): void {
    this.dialogRef.close(this.choice);
  }

  /** Closes with `undefined` — callers must read that as "do nothing". */
  cancel(): void {
    this.dialogRef.close();
  }
}
